import { SolvaceRumClient, getRumVersion, setRumClient } from './rum-client';
import { resolveRumIdentity } from './rum-identity';
import { SolvaceRumEnvironment } from './solvace-rum.types';

export const RUM_SDK_URL = 'https://www.datadoghq-browser-agent.com/us1/v7/datadog-rum.js';

interface DatadogRumInitConfiguration {
  applicationId: string;
  clientToken: string;
  site: string;
  service: string;
  env: string;
  sessionSampleRate: number;
  sessionReplaySampleRate: number;
  trackUserInteractions: boolean;
  trackResources: boolean;
  trackLongTasks: boolean;
  beforeSend: (event: { version?: string }) => boolean;
}

interface DatadogRumSdk extends SolvaceRumClient {
  init: (configuration: DatadogRumInitConfiguration) => void;
  getInitConfiguration: () => object | undefined;
  setGlobalContextProperty: (key: string, value: unknown) => void;
}

interface DatadogRumGlobal extends Partial<DatadogRumSdk> {
  q?: Array<() => void>;
  onReady: (callback: () => void) => void;
}

type RumWindow = Window & { DD_RUM?: DatadogRumGlobal };

export function initRum(env: SolvaceRumEnvironment, appName: string): void {
  const config = env?.datadogRum;
  const rumWindow = window as RumWindow;
  if (!config?.applicationId || !config.clientToken || env.isLocalhost || rumWindow.DD_RUM?.getInitConfiguration?.()) {
    return;
  }

  try {
    loadRumSdk(rumWindow).onReady(() => {
      try {
        const sdk = rumWindow.DD_RUM;
        if (!isLoadedSdk(sdk) || sdk.getInitConfiguration()) return;

        const { service, env: rumEnv } = resolveRumIdentity(location.host);
        sdk.init({
          applicationId: config.applicationId,
          clientToken: config.clientToken,
          site: 'datadoghq.com',
          service,
          env: rumEnv,
          sessionSampleRate: 100,
          sessionReplaySampleRate: 0,
          trackUserInteractions: true,
          trackResources: true,
          trackLongTasks: true,
          beforeSend: (event) => {
            const version = getRumVersion();
            if (version) event.version = version;
            return true;
          },
        });
        sdk.setGlobalContextProperty('solvace_app', appName);
        setRumClient(sdk);
      } catch (error) {
        reportInitError(env, error);
      }
    });
  } catch (error) {
    reportInitError(env, error);
  }
}

function loadRumSdk(rumWindow: RumWindow): DatadogRumGlobal {
  if (!rumWindow.DD_RUM) {
    const queue: Array<() => void> = [];
    rumWindow.DD_RUM = { q: queue, onReady: (callback) => queue.push(callback) };

    const script = document.createElement('script');
    script.async = true;
    script.src = RUM_SDK_URL;
    document.head.appendChild(script);
  }
  return rumWindow.DD_RUM;
}

function isLoadedSdk(sdk: DatadogRumGlobal | undefined): sdk is DatadogRumGlobal & DatadogRumSdk {
  return typeof sdk?.init === 'function' && typeof sdk.getInitConfiguration === 'function' && typeof sdk.addAction === 'function';
}

function reportInitError(env: SolvaceRumEnvironment, error: unknown): void {
  if (!env.production) {
    console.error('[RUM] Error initializing Datadog RUM:', error);
  }
}
